/**
 * @file SourcesInChatResponse.tsx
 * Renders the various sources (documents, laws, decisions, forms) associated with a chat response,
 * organized into tabs. It fetches feature flags to determine which source types are enabled.
 */
import {
  ExternalLink,
  AlertCircle,
  ScrollText,
  Link,
  Landmark,
  ChevronDown,
  ChevronUp,
  FileText,
} from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useI18n } from '@/locales/client';
import { ConversationAdvice } from '@/types/api/conversation.types';
import { featureFlagsSelectors } from '@/lib/store/feature-flags.store';
import { adviceService } from '@/lib/services';
import { Session } from 'next-auth';
import { useSession } from 'next-auth/react';
import { useCitationContext } from '@/lib/contexts/CitationContext';
import { CITATIONS_CONFIG } from '@/lib/config/citations.config';
import { extractCitationNumbers } from '@/lib/utils/citations';

// --- Helper Components for List Items ---

/**
 * Props for the DocumentItem component.
 */
interface DocumentItemProps {
  /** The id of the source */
  id: string;
  /** The URL of the document source. */
  url: string;
  /** The title of the document. */
  title: string;
  /** The source identifier (e.g., hostname). */
  source: string;

  session: Session;
}

/**
 * Renders a single document source item.
 * @param {DocumentItemProps} props - The component props.
 * @returns {JSX.Element} A link item representing a document source.
 */
const DocumentItem = React.memo(
  ({ url, title, source, id, session }: DocumentItemProps) => {
    return (
      <a
        href={url}
        target='_blank'
        rel='noopener noreferrer'
        onClick={(e) => {
          adviceService.incrementAdviceSourceClick(id, session);
        }}
        className='flex items-center gap-2 rounded-lg bg-purple-100 p-2 shadow-lg transition-all hover:bg-purple-200 dark:bg-purple-900/20 dark:shadow-gray-900/10 dark:hover:bg-purple-900/30 sm:gap-3 sm:p-4'
      >
        <span className='inline-block rounded-full bg-purple-200 p-1 text-purple-700 dark:bg-purple-800/50 dark:text-purple-300 sm:p-2'>
          <Link className='h-3.5 w-3.5 text-purple-600 dark:text-purple-400 sm:h-5 sm:w-5' />
        </span>
        <div className='min-w-0 flex-1 overflow-hidden'>
          <div className='line-clamp-2 text-xs font-semibold leading-tight text-gray-700 dark:text-gray-100 sm:text-base'>
            {title || url}
          </div>
          <div className='line-clamp-2 text-xs text-gray-500 dark:text-gray-400 sm:text-sm'>
            {source || new URL(url).hostname}
          </div>
        </div>
        <div className='ml-auto flex-shrink-0 text-purple-600 transition hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300'>
          <ExternalLink className='h-3.5 w-3.5 sm:h-5 sm:w-5' />
        </div>
      </a>
    );
  }
);
DocumentItem.displayName = 'DocumentItem';

/**
 * Props for the LawItem component.
 */
interface LawItemProps {
  /** The id of the source */
  id: string;
  /** The law source object (consider defining a more specific type). */
  lawSource: any;
  /** The modification data object, if the law source was modified. */
  modificationData: { url?: string; pageSource?: string; pageTitle?: string; content?: string; filename?: string } | null;
  /** The translation function. */
  t: (key: string, options?: any) => string;
  session: Session;
}

/**
 * Renders a single law source item, including potential modification details.
 * @param {LawItemProps} props - The component props.
 * @returns {JSX.Element} A details/summary item representing a law source.
 */
const LawItem = React.memo(
  ({ lawSource, modificationData, t, id, session }: LawItemProps) => {
    const modified = lawSource.isModified || false;
    const url = lawSource.url || '#';
    const pageSource = lawSource.pageSource || 'Code civil du Québec';
    const pageTitle = lawSource.pageTitle || 'Article du Code civil';
    const originalText = lawSource.content || '';
    const modificationText = modificationData?.content || null;
    const modificationUrl = modificationData?.url || null;

    return (
      <div
        className={`flex w-full items-start gap-2 rounded-lg p-2 shadow-lg transition-all dark:shadow-gray-900/10 sm:gap-3 sm:p-4 ${
          modified
            ? 'bg-red-100 hover:bg-red-200 dark:bg-red-900/20 dark:hover:bg-red-900/30'
            : 'bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/20 dark:hover:bg-purple-900/30'
        }`}
      >
        <details
          className='w-full'
          onToggle={(e) => {
            const details = e.currentTarget as HTMLDetailsElement;
            if (details.open) {
              adviceService.incrementAdviceSourceClick(id, session);
            }
          }}
        >
          <summary
            className={`flex cursor-pointer items-center gap-1.5 transition sm:gap-2 ${
              modified
                ? 'text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300'
                : 'text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300'
            }`}
          >
            <span
              className={`inline-block rounded-full p-1 sm:p-2 ${
                modified
                  ? 'bg-red-200 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                  : 'bg-purple-200 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
              }`}
            >
              <ScrollText
                strokeWidth={2.5}
                className='h-3.5 w-3.5 sm:h-5 sm:w-5'
              />
            </span>
            <div className='min-w-0 flex-1'>
              <div className='flex items-center overflow-hidden text-ellipsis text-xs font-semibold text-gray-700 dark:text-gray-100 sm:text-base'>
                <a
                  href={url}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={(e) => {
                    e.stopPropagation(); // preserve summary toggle behavior
                    adviceService.incrementAdviceSourceClick(id, session);
                  }}
                  className='flex items-center gap-1 hover:underline'
                >
                  {pageSource}
                </a>

                <ChevronDown
                  size={12}
                  className='ml-1 flex-shrink-0 text-gray-500 dark:text-gray-300 sm:h-4 sm:w-4'
                />

                {modified && (
                  <AlertCircle
                    strokeWidth={2.5}
                    className='ml-1 h-3.5 w-3.5 flex-shrink-0 text-red-600 dark:text-red-400 sm:ml-2 sm:h-5 sm:w-5'
                  />
                )}
              </div>

              <div className='truncate text-xs text-gray-500 dark:text-gray-400 sm:text-sm'>
                {pageTitle}
              </div>
            </div>

            <a
              href={url}
              target='_blank'
              rel='noopener noreferrer'
              onClick={(e) => {
                e.stopPropagation(); // preserve summary toggle behavior
                adviceService.incrementAdviceSourceClick(id, session);
              }}
              className={`ml-auto flex-shrink-0 transition ${
                modified
                  ? 'text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300'
                  : 'text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300'
              }`}
              aria-label={'View Source'}
            >
              <ExternalLink className='h-3.5 w-3.5 sm:h-5 sm:w-5' />
            </a>
          </summary>
          <div className='mt-2 rounded-lg border border-gray-200 bg-gray-50 p-2 shadow-inner dark:border-gray-700/50 dark:bg-gray-900/30 sm:mt-3 sm:p-4'>
            {modified && modificationText && (
              <div className='mb-3 rounded-lg border-2 border-red-200 bg-red-50 p-2 dark:border-red-800/50 dark:bg-red-900/20 sm:mb-4 sm:p-3'>
                <h2 className='mb-1 flex items-center gap-1 text-xs font-semibold text-red-700 dark:text-red-400 sm:mb-1.5 sm:text-sm'>
                  {t('sourcesInChat.modification')}
                  {pageTitle ? `: ${pageTitle}` : ''}
                  {modificationUrl && (
                    <a
                      href={modificationUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='ml-1 inline-flex text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300'
                      aria-label='View modification source'
                    >
                      <ExternalLink className='h-3 w-3 sm:h-3.5 sm:w-3.5' />
                    </a>
                  )}
                </h2>
                <div className='whitespace-pre-line text-xs text-gray-700 dark:text-gray-100 sm:text-sm'>
                  <Markdown remarkPlugins={[remarkGfm]}>
                    {modificationText}
                  </Markdown>
                </div>
              </div>
            )}
            <div className='whitespace-pre-line text-xs text-gray-700 dark:text-gray-100 sm:text-base'>
              <Markdown remarkPlugins={[remarkGfm]}>{originalText}</Markdown>
            </div>
          </div>
        </details>
      </div>
    );
  }
);
LawItem.displayName = 'LawItem';

/**
 * Props for the DecisionItem component.
 */
interface DecisionItemProps {
  /** The id of the source */
  id: string;
  /** The URL of the decision source, if available. */
  url: string | undefined;
  /** The reference identifier for the decision. */
  reference: string;
  /** The names of the parties involved in the decision, if available. */
  partyNames: string | undefined;
  session: Session;
}

/**
 * Renders a single case law (decision) source item.
 * @param {DecisionItemProps} props - The component props.
 * @returns {JSX.Element} A link item representing a decision source.
 */
const DecisionItem = React.memo(
  ({ url, reference, partyNames, session, id }: DecisionItemProps) => {
    return (
      <a
        href={url || '#'}
        target='_blank'
        onClick={() => {
          adviceService.incrementAdviceSourceClick(id, session);
        }}
        rel='noopener noreferrer'
        className='flex w-full items-center gap-2 rounded-lg bg-purple-100 p-2 shadow-lg transition-all hover:bg-purple-200 dark:bg-purple-900/20 dark:shadow-gray-900/10 dark:hover:bg-purple-900/30 sm:gap-4 sm:p-4'
      >
        <span className='inline-flex flex-shrink-0 items-center justify-center rounded-full bg-purple-200 p-1 text-purple-700 dark:bg-purple-800/50 dark:text-purple-300 sm:p-2.5'>
          <Landmark
            strokeWidth={2.5}
            className='h-3.5 w-3.5 text-purple-600 dark:text-purple-400 sm:h-5 sm:w-5'
          />
        </span>
        <div className='min-w-0 flex-1 overflow-hidden'>
          <div className='line-clamp-2 text-xs font-semibold leading-tight text-gray-700 dark:text-gray-100 sm:text-base'>
            {reference}
          </div>
          <div className='line-clamp-2 text-xs font-semibold leading-tight text-gray-700 dark:text-gray-100 sm:text-base'>
            {partyNames || 'Québec Case Law'}
          </div>
        </div>
        <div className='ml-auto flex-shrink-0 text-purple-600 transition hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300'>
          <ExternalLink className='h-3.5 w-3.5 sm:h-5 sm:w-5' />
        </div>
      </a>
    );
  }
);
DecisionItem.displayName = 'DecisionItem';

/**
 * Props for the FormItem component.
 */
interface FormItemProps {
  /** The id of the source */
  id: string;
  /** The URL of the form (document or page). */
  url: string;
  /** The title of the form. */
  title: string;
  /** A brief description or content snippet of the form. */
  content: string;
  session: Session;
}

/**
 * Renders a single legal form source item.
 * @param {FormItemProps} props - The component props.
 * @returns {JSX.Element} A link item representing a form source.
 */
const FormItem = React.memo(
  ({ url, title, content, session, id }: FormItemProps) => {
    return (
      <a
        href={url}
        target='_blank'
        onClick={() => {
          adviceService.incrementAdviceSourceClick(id, session);
        }}
        rel='noopener noreferrer'
        className='flex w-full max-w-full items-center gap-2 overflow-hidden rounded-lg bg-purple-100 p-2 shadow-lg transition-all hover:bg-purple-200 dark:bg-purple-900/20 dark:shadow-gray-900/10 dark:hover:bg-purple-900/30 sm:gap-4 sm:p-4'
      >
        <span className='inline-flex flex-shrink-0 items-center justify-center rounded-full bg-purple-200 p-1 text-purple-700 dark:bg-purple-800/50 dark:text-purple-300 sm:p-2.5'>
          <FileText
            strokeWidth={2.5}
            className='h-3.5 w-3.5 text-purple-600 dark:text-purple-400 sm:h-5 sm:w-5'
          />
        </span>
        <div className='min-w-0 flex-1 overflow-hidden'>
          <div className='line-clamp-2 text-xs font-semibold leading-tight text-gray-700 dark:text-gray-100 sm:text-base'>
            {title || 'Legal Form'}
          </div>
          <div className='line-clamp-2 text-xs text-gray-500 dark:text-gray-400 sm:text-sm'>
            {content || 'Relevant legal document'}
          </div>
        </div>
        <div className='ml-auto flex-shrink-0 text-purple-600 transition hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300'>
          <ExternalLink className='h-3.5 w-3.5 sm:h-5 sm:w-5' />
        </div>
      </a>
    );
  }
);
FormItem.displayName = 'FormItem';

// --- Main Component ---

/**
 * Props for the SourcesInChatResponse component.
 */
interface SourcesProps {
  /** The conversation advice object containing sources. */
  advice: ConversationAdvice;
}

/**
 * Main component to display various sources (documents, laws, decisions, forms)
 * associated with a chat advice response, organized into filterable tabs.
 * @param {SourcesProps} props - The component props.
 * @returns {JSX.Element | null} The rendered sources section or null if no sources are available/enabled.
 */
export const SourcesInChatResponse = React.memo(({ advice }: SourcesProps) => {
  const t = useI18n();
  const { data: session } = useSession();

  // Citation context — may be null if rendered outside CitationProvider
  const citationCtx = useCitationContext();
  const adviceId = citationCtx?.adviceId || '';

  /**
   * Memoized array of sources extracted from the advice prop.
   */
  const sources = useMemo(() => advice?.sources || [], [advice?.sources]);

  /**
   * Whether the response is still being streamed (not yet finalized).
   */
  const isStillStreaming = advice?.status === 'STREAMING' || advice?.status === 'SUBMITTED';

  /**
   * Set of citation numbers that actually appear in the AI response text.
   * Used to filter out uncited sources once the response is complete.
   * During streaming, this is null (show all sources until response finishes).
   */
  const citedNumbers = useMemo(() => {
    if (isStillStreaming) return null; // Don't filter while still streaming
    const responseText = advice?.advice || advice?.answer || '';
    if (!responseText) return null; // No text yet — show all
    const nums = extractCitationNumbers(responseText);
    return nums.length > 0 ? new Set(nums) : null; // null = no filtering (e.g. citations disabled)
  }, [advice?.advice, advice?.answer, isStillStreaming]);

  /**
   * Filters sources to only those actually cited in the response.
   * Forms are exempt — they always pass through.
   * During streaming or when no citations exist, all sources pass through.
   */
  const isCitedSource = useCallback(
    (source: { citationIndex?: number | null; type: string }) => {
      if (!citedNumbers) return true; // No filtering active
      if (source.type === 'FORM') return true; // Forms always shown
      return source.citationIndex != null && citedNumbers.has(source.citationIndex);
    },
    [citedNumbers]
  );

  /**
   * Memoized array of law sources (type LAW) — filtered to only cited sources.
   */
  const lawSources = useMemo(
    () => sources.filter((s) => s.type === 'LAW' && isCitedSource(s)),
    [sources, isCitedSource]
  );
  /**
   * Memoized array of modified law sources (type MODIFIED_LAW) — filtered to only cited sources.
   */
  const modifiedLawSources = useMemo(
    () => sources.filter((s) => s.type === 'MODIFIED_LAW' && isCitedSource(s)),
    [sources, isCitedSource]
  );
  /**
   * Memoized array of document sources (type INFO) — filtered to only cited sources.
   */
  const documentSources = useMemo(
    () => sources.filter((s) => s.type === 'INFO' && isCitedSource(s)),
    [sources, isCitedSource]
  );
  /**
   * Memoized array of decision sources (type CASELAW) — filtered to only cited sources.
   */
  const decisionSources = useMemo(
    () => sources.filter((s) => s.type === 'CASELAW' && isCitedSource(s)),
    [sources, isCitedSource]
  );
  /**
   * Memoized array of form sources (type FORM) — NOT filtered (always shown).
   */
  const formSources = useMemo(
    () => sources.filter((s) => s.type === 'FORM'),
    [sources]
  );

  /**
   * State to control whether all items in a tab are shown or just the first few.
   */
  const [showAll, setShowAll] = useState(false);
  /**
   * Callback to toggle the `showAll` state.
   */
  const toggleShowAll = useCallback(() => setShowAll((prev) => !prev), []);

  /**
   * State to manage the currently active source tab (e.g., 'laws', 'documents').
   */
  const [activeTab, setActiveTab] = useState('laws'); // Default to laws

  // Sync activeTab from CitationContext when a badge click requests a tab switch
  useEffect(() => {
    if (citationCtx?.requestedTab) {
      setActiveTab(citationCtx.requestedTab);
      setShowAll(true); // Expand to show all items so the target is visible
    }
  }, [citationCtx?.requestedTab, citationCtx?.requestSeq]);

  // Determine available tabs based on filtered sources and feature flags
  const hasLaws = lawSources.length > 0;
  const hasDocuments = documentSources.length > 0;
  const hasDecisions = decisionSources.length > 0;
  const hasForms = formSources.length > 0;

  const shouldShowLaws =
    hasLaws && featureFlagsSelectors.isFeatureEnabled('CHAT_SOURCES_LAWS');
  const shouldShowDecisions =
    hasDecisions &&
    featureFlagsSelectors.isFeatureEnabled('CHAT_SOURCES_CASE_LAW');
  const shouldShowDocuments =
    hasDocuments &&
    featureFlagsSelectors.isFeatureEnabled('CHAT_SOURCES_DOCUMENTS');
  const shouldShowLegalForms =
    hasForms &&
    featureFlagsSelectors.isFeatureEnabled('CHAT_SOURCES_LEGAL_FORMS');

  /**
   * Effect to initialize the active tab based on available source types
   * when the component mounts or the availability of sources changes.
   * It prioritizes tabs in the order: laws > documents > decisions > forms.
   * Also resets the `showAll` state when the active tab might change.
   */
  useEffect(() => {
    let initialTab = '';
    if (shouldShowLaws) {
      initialTab = 'laws';
    } else if (shouldShowDocuments) {
      initialTab = 'documents';
    } else if (shouldShowDecisions) {
      initialTab = 'decisions';
    } else if (shouldShowLegalForms) {
      initialTab = 'forms';
    }
    setActiveTab(initialTab);
    setShowAll(false); // Reset showAll when available tabs change
  }, [
    shouldShowLaws,
    shouldShowDocuments,
    shouldShowDecisions,
    shouldShowLegalForms,
  ]);

  /**
   * Renders the content for the 'Documents' tab.
   * @returns {JSX.Element} The list of document items or a 'not available' message.
   */
  const renderDocumentsTab = () => {
    if (!hasDocuments) {
      return (
        <p className='px-2 text-gray-500 dark:text-gray-400'>
          {t('sourcesInChat.tabsContent.noDocuments')}
        </p>
      );
    }

    const visibleDocumentSources = showAll
      ? documentSources
      : documentSources.slice(0, 3);

    return (
      <div className='mt-2 space-y-2 sm:mt-3 sm:space-y-3'>
        {session &&
          visibleDocumentSources.map((source: any) => (
            <div
              key={source.url}
              id={
                source.citationIndex != null
                  ? `${CITATIONS_CONFIG.SOURCE_DOM_ID_PREFIX}${adviceId}-${source.citationIndex}`
                  : undefined
              }
            >
              <DocumentItem
                url={source.url}
                title={source.pageTitle || source.title || 'Source'}
                source={
                  source.pageSource ||
                  (source.url ? new URL(source.url).hostname : 'Web Source')
                }
                id={source.id}
                session={session}
              />
            </div>
          ))}
        {documentSources.length > 3 && (
          <button
            onClick={toggleShowAll}
            className='mt-2 flex w-full items-center justify-center gap-1 rounded-lg bg-purple-600 p-1 text-xs text-white transition-colors hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 sm:gap-2 sm:p-2 sm:text-sm'
          >
            {showAll ? (
              <>
                {t('sourcesInChat.showLess')}
                <ChevronUp size={12} className='sm:h-4 sm:w-4' />
              </>
            ) : (
              <>
                {t('sourcesInChat.showMore')}
                <ChevronDown size={12} className='sm:h-4 sm:w-4' />
              </>
            )}
          </button>
        )}
      </div>
    );
  };

  /**
   * Renders the content for the 'Laws' tab.
   * @returns {JSX.Element} The list of law items or a 'not available' message.
   */
  const renderLawsTab = () => {
    if (!hasLaws) {
      return (
        <p className='px-2 text-gray-500 dark:text-gray-400'>
          {t('sourcesInChat.tabsContent.noLaws')}
        </p>
      );
    }

    const totalLaws = lawSources.length;
    const visibleLawSources = showAll ? lawSources : lawSources.slice(0, 3);

    return (
      <div className='mt-2 space-y-2 sm:mt-3 sm:space-y-3'>
        {session &&
          visibleLawSources.map((lawSource: any) => {
            // Prefer modification object directly on LAW source (new Lambda format),
            // fall back to separate MODIFIED_LAW lookup (legacy DB-loaded data).
            const modificationData = (() => {
              // New format: modification is an object { url, content, pageTitle, ... }
              if (lawSource.modification && typeof lawSource.modification === 'object') {
                return lawSource.modification;
              }
              // Legacy format: look up separate MODIFIED_LAW source by originalSourceId
              const legacyMod = modifiedLawSources.find(
                (modSource: any) => modSource.originalSourceId === lawSource.id
              );
              if (legacyMod?.content) {
                return { content: legacyMod.content, url: legacyMod.url };
              }
              return null;
            })();

            // Ensure lawSource has a unique ID for the key, fallback if needed
            const itemKey =
              lawSource.id ||
              `${lawSource.url}-${lawSource.pageTitle || 'law'}`;

            return (
              <div
                key={itemKey}
                id={
                  lawSource.citationIndex != null
                    ? `${CITATIONS_CONFIG.SOURCE_DOM_ID_PREFIX}${adviceId}-${lawSource.citationIndex}`
                    : undefined
                }
              >
                <LawItem
                  lawSource={lawSource}
                  modificationData={modificationData}
                  t={t}
                  id={lawSource.id}
                  session={session}
                />
              </div>
            );
          })}

        {totalLaws > 3 && (
          <button
            onClick={toggleShowAll}
            className='mt-2 flex w-full items-center justify-center gap-1 rounded-lg bg-purple-600 p-1 text-xs text-white transition-colors hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 sm:gap-2 sm:p-2 sm:text-sm'
          >
            {showAll ? (
              <>
                {t('sourcesInChat.showLess')}
                <ChevronUp size={12} className='sm:h-4 sm:w-4' />
              </>
            ) : (
              <>
                {t('sourcesInChat.showMore')}
                <ChevronDown size={12} className='sm:h-4 sm:w-4' />
              </>
            )}
          </button>
        )}
      </div>
    );
  };

  /**
   * Renders the content for the 'Decisions' tab.
   * @returns {JSX.Element} The list of decision items or a 'not available' message.
   */
  const renderDecisionsTab = () => {
    if (!hasDecisions) {
      return (
        <p className='px-2 text-gray-500 dark:text-gray-400'>
          {t('sourcesInChat.tabsContent.noDecisions')}
        </p>
      );
    }

    const visibleDecisionSources = showAll
      ? decisionSources
      : decisionSources.slice(0, 3);

    return (
      <div className='mt-2 space-y-2 sm:mt-3 sm:space-y-3'>
        {session &&
          visibleDecisionSources.map((source: any) => (
            <div
              key={source.reference}
              id={
                source.citationIndex != null
                  ? `${CITATIONS_CONFIG.SOURCE_DOM_ID_PREFIX}${adviceId}-${source.citationIndex}`
                  : undefined
              }
            >
              <DecisionItem
                url={source.url}
                reference={source.reference}
                partyNames={source.nomsDesPartis}
                id={source.id}
                session={session}
              />
            </div>
          ))}
        {decisionSources.length > 3 && (
          <button
            onClick={toggleShowAll}
            className='mt-2 flex w-full items-center justify-center gap-1 rounded-lg bg-purple-600 p-1 text-xs text-white transition-colors hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 sm:gap-2 sm:p-2 sm:text-sm'
          >
            {showAll ? (
              <>
                {t('sourcesInChat.showLess')}
                <ChevronUp size={12} className='sm:h-4 sm:w-4' />
              </>
            ) : (
              <>
                {t('sourcesInChat.showMore')}
                <ChevronDown size={12} className='sm:h-4 sm:w-4' />
              </>
            )}
          </button>
        )}
      </div>
    );
  };

  /**
   * Renders the content for the 'Forms' tab.
   * @returns {JSX.Element} The list of form items or a 'not available' message.
   */
  const renderFormsTab = () => {
    if (!hasForms) {
      return (
        <p className='px-2 text-gray-500 dark:text-gray-400'>
          {t('sourcesInChat.tabsContent.noForms')}
        </p>
      );
    }

    const visibleFormSources = showAll ? formSources : formSources.slice(0, 3);

    return (
      <div className='mt-2 space-y-2 sm:mt-3 sm:space-y-3'>
        {session &&
          visibleFormSources.map((source: any) => (
            <div
              key={source.docUrl || source.pageUrl}
              id={
                source.citationIndex != null
                  ? `${CITATIONS_CONFIG.SOURCE_DOM_ID_PREFIX}${adviceId}-${source.citationIndex}`
                  : undefined
              }
            >
              <FormItem
                url={source.docUrl || source.pageUrl || '#'}
                title={source.title || source.filename || 'Legal Form'}
                content={source.content || 'Relevant legal document'}
                id={source.id}
                session={session}
              />
            </div>
          ))}
        {formSources.length > 3 && (
          <button
            onClick={toggleShowAll}
            className='mt-2 flex w-full items-center justify-center gap-1 rounded-lg bg-purple-600 p-1 text-xs text-white transition-colors hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 sm:gap-2 sm:p-2 sm:text-sm'
          >
            {showAll ? (
              <>
                {t('sourcesInChat.showLess')}
                <ChevronUp size={12} className='sm:h-4 sm:w-4' />
              </>
            ) : (
              <>
                {t('sourcesInChat.showMore')}
                <ChevronDown size={12} className='sm:h-4 sm:w-4' />
              </>
            )}
          </button>
        )}
      </div>
    );
  };

  // Log available tabs for debugging
  useEffect(() => {
    console.log('[SourcesInChatResponse] Available tabs:', {
      hasLaws,
      hasDocuments,
      hasDecisions,
      hasForms,
      activeTab,
      // **REMOVED**: formsData log
      // formsData: processedAdvice?.relevant_forms,
    });
  }, [
    shouldShowLaws,
    shouldShowDocuments,
    shouldShowDecisions,
    shouldShowLegalForms,
    activeTab,
    // **REMOVED**: processedAdvice?.relevant_forms dependency
    // processedAdvice?.relevant_forms,
    // Add missing dependencies
    hasLaws,
    hasDocuments,
    hasDecisions,
    hasForms,
  ]);

  // Return null if no sources are available at all
  if (
    !shouldShowDocuments &&
    !shouldShowLaws &&
    !shouldShowDecisions &&
    !shouldShowLegalForms
  ) {
    return null;
  }

  return (
    <div className='w-full space-y-2 pb-3 text-gray-500 dark:text-gray-400 sm:space-y-4 sm:pb-6'>
      {/* Tab Buttons */}
      <div className='scrollbar-hide flex gap-1 overflow-x-auto border-b-2 pb-1 sm:gap-4 sm:pb-2'>
        {[
          {
            key: 'laws',
            condition: shouldShowLaws,
            label: t('sourcesInChat.tabsTitle.laws'),
          },
          {
            key: 'documents',
            condition: shouldShowDocuments,
            label: t('sourcesInChat.tabsTitle.documents'),
          },
          {
            key: 'decisions',
            condition: shouldShowDecisions,
            label: t('sourcesInChat.tabsTitle.decisions'),
          },
          {
            key: 'forms',
            condition: shouldShowLegalForms, // Forms tab moved to the end
            label: t('sourcesInChat.tabsTitle.forms'),
          },
        ]
          .filter(({ condition }) => condition)
          .map(({ key, label }) => {
            const isActive = activeTab === key;

            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex-shrink-0 whitespace-nowrap px-2 py-1 text-sm font-semibold sm:px-3 sm:py-2 sm:text-base ${
                  isActive
                    ? 'max-w-none border-b-2 border-purple-600 text-purple-600 dark:border-purple-400 dark:text-purple-400'
                    : 'overflow-hidden text-ellipsis text-gray-600 dark:text-gray-300'
                }`}
              >
                {label}
              </button>
            );
          })}
      </div>

      {/* Tab Content */}
      {activeTab === 'documents' && shouldShowDocuments && renderDocumentsTab()}
      {activeTab === 'laws' && shouldShowLaws && renderLawsTab()}
      {activeTab === 'decisions' && shouldShowDecisions && renderDecisionsTab()}
      {activeTab === 'forms' && shouldShowLegalForms && renderFormsTab()}
    </div>
  );
});

SourcesInChatResponse.displayName = 'SourcesInChatResponse';
